import { ACKNOWLEDGE_STATUS_CONFIG } from '../purchasing/lineStatus';
import { isClosedRequestRow, isProposalRow, isWithdrawalRow, markedOutcome } from '../purchasing/changelogFields';

/**
 * The words Audit History uses for a PO line's acknowledge statuses and for
 * the outcome of a decision, and how a row is classified for its badge.
 *
 * Split out of Changelog.tsx (CR-7408 rework), which was past the repo's
 * 500-line limit.
 */

/** The fields of an audit row that classifying it reads. */
export interface ChangelogEventFields {
  field_changed: string;
  old_value: string | null;
  new_value: string;
  reason?: string;
  event_type?: string;
}

const statusWord = (status: string): string => ACKNOWLEDGE_STATUS_CONFIG[status].label;

/**
 * CR-4680: human-readable labels for acknowledge_status values.
 *
 * Where a word names the line's STATUS it is the status badge's own word,
 * read from lineStatus — an acknowledged line read "New → Accepted" here
 * under a badge saying WIP (CR-7408 rework, tester 7 Oct), and a declined
 * request read "Declined" against the badge's "Change Declined" (Aaron,
 * 8 Oct: "it should match the status which is Change Declined"). The two
 * proposal words stay narrative: they say WHO proposed, which the badge's
 * "Changed" does not.
 */
export const ACK_STATUS_DISPLAY: Record<string, string> = {
  new: statusWord('new'),
  accepted: statusWord('accepted'),
  wip: statusWord('wip'),
  change_proposed_supplier: 'Proposed by Supplier',
  change_proposed_customer: 'Proposed by Customer',
  change_declined: statusWord('change_declined'),
  declined: statusWord('declined'),
  despatched: statusWord('despatched'),
  cancelled: statusWord('cancelled'),
  closed: statusWord('closed'),
};

/**
 * The words the SCHEDULE grain's Audit History used before CR-7408 rework, kept
 * as they were: schedule lines are left alone (GUIDE, Aaron 5 Oct 2026), and the
 * schedule modals render this same component (round 1, blind D1).
 */
export const SCHEDULE_ACK_STATUS_DISPLAY: Record<string, string> = {
  new: 'New',
  accepted: 'Accepted',
  change_proposed_supplier: 'Proposed by Supplier',
  change_proposed_customer: 'Proposed by Customer',
  change_declined: 'Declined',
  declined: 'Declined',
  wip: 'Work In Progress',
  despatched: 'Dispatched',
  cancelled: 'Cancelled',
  closed: 'Closed',
};

export const SCHEDULE_DECISION_OUTCOME_LABEL: Record<string, string> = {
  change_accepted: 'Accepted',
  change_declined: 'Declined',
  change_cancelled: 'Cancelled',
};

/**
 * The outcome a decision row resolved to, for the right-hand side of the
 * value line. When other proposals on the line are still open the backend
 * leaves acknowledge_status unmoved, so the row has no recorded outcome to
 * show — the decision is only implied by the reason text.
 */
export const DECISION_OUTCOME_LABEL: Record<string, string> = {
  change_accepted: 'Accepted',
  change_declined: statusWord('change_declined'),
  change_cancelled: 'Cancelled',
};

/** Which badge an audit row wears (a key of Changelog's EVENT_TYPE_CONFIG). */
export function getEventKey(entry: ChangelogEventFields): string {
  // CR-4731: Show "Change Accepted" (green) for accepted changes
  const reason = (entry.reason || '').toLowerCase();
  if (entry.event_type === 'change_request') {
    // new_value is the authoritative outcome, so test it before the free-text
    // reason — a user-entered decline reason can legitimately contain the
    // word "accepted" ("not accepted by planning") and would otherwise win.
    // `new -> accepted` is the order being acknowledged; every other
    // transition into `accepted` resolves a proposal somebody made.
    if (entry.new_value === 'accepted' && entry.old_value === 'new') return 'order_acknowledged';
    // EXPEDITING-3: the row an Acknowledge Decline wrote, by its own marker.
    if (isClosedRequestRow(entry)) return 'request_closed';
    // CR-7408 rework: a withdrawal also lands on `accepted`; its marker wins.
    if (isWithdrawalRow(entry)) return 'change_cancelled';
    if (entry.new_value === 'accepted') return 'change_accepted';
    if (entry.new_value === 'change_declined') return 'change_declined';
    // CR-8596: the proposal row is the request itself, whatever the proposer
    // wrote in it ("will be rejected", "the last one was accepted"). Only an
    // ANSWER row may read as a decision below.
    if (isProposalRow(entry)) return 'change_request';
    // CR-8590: an answer is badged by its own marker ("Quantity change
    // accepted: …"), never by the comment after it ("previously declined").
    const marked = markedOutcome(entry);
    if (marked) return `change_${marked}`;
    if (/\b(rejected|declined)\b/.test(reason)) return 'change_declined';
    if (/\bcancelled\b/.test(reason)) return 'change_cancelled';
    if (reason.includes('accepted')) return 'change_accepted';
  }
  if (entry.field_changed === 'supplier_promise_date' && reason.includes('accepted') && !isProposalRow(entry)) {
    return 'change_accepted';
  }
  return entry.event_type || 'field_change';
}
